/* eslint-disable i18next/no-literal-string */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./SearchInput.module.scss";
import classnames from "classnames";
import { Svg } from "../Svg";

interface SearchInputProps {
  value: string;
  className?: string;
  inputClassName?: string;
  collapsible?: boolean;
  onChange?: (value: string) => void;
  onClear?: VoidFunction;
  placeholder?: string;
  "data-testid"?: string;
  isDisabled?: boolean;
  clearAllContainerClassName?: string;
  autoFocus?: boolean;
}

export interface SearchInputRef {
  focus: () => void;
}

export const SearchInput = forwardRef<SearchInputRef, SearchInputProps>((props, ref) => {
  const [collapsed, setCollapsed] = useState(Boolean(props.collapsible));
  const [disabled, setDisabled] = useState(Boolean(props.isDisabled));
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFocusRef = useRef({ hasFocus: false, setAt: 0 });
  const focus = useCallback(() => inputRef.current?.focus(), [inputRef]);
  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => props.onChange?.(e.target.value), [props]);

  const clear = useCallback(() => {
    if (props.isDisabled) {
      return;
    }
    if (props.onClear) {
      props.onClear();
    } else {
      props.onChange?.("");
    }
    focus();
  }, [props, focus]);

  const clickSearch = useCallback(() => {
    if (props.isDisabled) {
      return;
    }
    setCollapsed(!collapsed);
    focus();
  }, [collapsed, focus, props.isDisabled]);

  useEffect(() => {
    const hasFocus = document.activeElement === inputRef.current;
    if (props.autoFocus) {
      inputRef.current?.focus();
    }
    if (!props.isDisabled && hasFocusRef.current.hasFocus && Date.now() - hasFocusRef.current.setAt < 2500) {
      setTimeout(() => inputRef.current?.focus());
    }
    hasFocusRef.current = { hasFocus, setAt: Date.now() };
    setDisabled(Boolean(props.isDisabled));
  }, [props.isDisabled, props.autoFocus]);

  useImperativeHandle(ref, () => ({ focus }));

  return (
    <div className={classnames(styles.container, props.className)}>
      <input
        value={props.value}
        className={classnames(styles.searchInput, { [styles.collapsed]: props.collapsible && collapsed && !props.value }, props.inputClassName)}
        ref={inputRef}
        onChange={onChange}
        placeholder={props.placeholder}
        onFocus={() => setCollapsed(false)}
        onBlur={() => setCollapsed(true)}
        data-testid={props["data-testid"]}
        disabled={disabled}
      />
      <Svg iconName={`search-actions`} className={styles.searchIcon} onClick={clickSearch} />
      {props.value && (
        <div className={classnames(styles.clearSearchInputContainer, props.clearAllContainerClassName || "")}>
          <Svg iconName={`close`} className={styles.searchCancelIcon} onClick={clear} data-testid={"clear-search-input"} />
        </div>
      )}
    </div>
  );
});
